import { apiClient } from './client';

export function searchShops (params: { search?: string; page?: number; page_size?: number; signal?: AbortSignal }) {
  const search = params.search?.trim() ?? '';
  const numericId = parseInt(search, 10);
  const isNumericId = !isNaN(numericId) && numericId.toString() === search;

  const queryParams = isNumericId
    ? { id: numericId, page: params.page, page_size: params.page_size }
    : {
        search,
        page: params.page,
        page_size: params.page_size,
      };

  return apiClient.get('/shops/search', { params: queryParams, signal: params.signal });
}

export function getAllActiveIds () {
  return apiClient.get('/shops/active-ids');
}

export function getShop (shopId: number | string) {
  return apiClient.get(`/${shopId}/shop`);
}

export function getShopBasic (shopId: number | string) {
  return apiClient.get(`/${shopId}/shop`);
}

export function createShop (payload: Record<string, unknown>) {
  return apiClient.post('/shops', payload);
}

export function updateShop (shopId: number | string, payload: Record<string, unknown>) {
  return apiClient.patch(`/${shopId}/shop`, payload);
}

export function updateShopAddress (shopId: number | string, payload: Record<string, unknown>) {
  return apiClient.patch(`/${shopId}/shop/address`, payload);
}

export function activateShop (shopId: number | string, payload?: Record<string, unknown>) {
  return apiClient.post(`/${shopId}/shop/activate`, payload);
}

export function deactivateShop (shopId: number | string, payload?: Record<string, unknown>) {
  return apiClient.post(`/${shopId}/shop/deactivate`, payload);
}

export function getShopDiagnostics (shopId: number | string) {
  return apiClient.get(`/${shopId}/shop/diagnostics`);
}

export function getActivationDiagnostics (shopId: number | string) {
  return apiClient.get(`/${shopId}/shop/activation-diagnostics`);
}

export function getShopAppEmbeds (shopId: number | string) {
  return apiClient.get(`/${shopId}/shop/theme/app-embeds`);
}

export interface ShopWebPixel {
  installed: boolean;
  id: string | null;
  settings: Record<string, unknown> | null;
}

export interface ShopWebPixelPreview {
  settings: Record<string, unknown>;
}

export function getShopWebPixel (shopId: number | string) {
  return apiClient.get<ShopWebPixel>(`/${shopId}/shop/web-pixel`);
}

export function getShopWebPixelPreview (shopId: number | string) {
  return apiClient.get<ShopWebPixelPreview>(`/${shopId}/shop/web-pixel/preview`);
}

export function createShopWebPixel (shopId: number | string) {
  return apiClient.post(`/${shopId}/shop/web-pixel`);
}

export function deleteShopWebPixel (shopId: number | string) {
  return apiClient.delete(`/${shopId}/shop/web-pixel`);
}

export function batchUpdateSettings (shopId: number | string, settings: unknown[]) {
  return apiClient.post(`/${shopId}/shop/settings/batch`, { settings });
}

export function getPlans () {
  return apiClient.get('/shops/billing/plans');
}

export type CreateLoopAnywhereShopPayload = {
  name: string;
  email: string;
  currency: string;
  address1: string;
  city: string;
  country: string;
  country_code: string;
  subdomain: string;
  opportunity_id: string;
  sender_name: string;
  support_email: string;
  from_email: string;
  address2?: string;
  region?: string;
  postal_code?: string;
  phone?: string;
};

export type CreateLoopAnywhereShopResponse = {
  message: string;
  name: string;
  shopId: number;
};

export type ActivateLoopAnywhereShopResponse = {
  message: string;
  shopId: number;
};

export function buildCreateLoopAnywhereShopPayload (
  form: Record<keyof CreateLoopAnywhereShopPayload, string>,
): CreateLoopAnywhereShopPayload {
  const payload: CreateLoopAnywhereShopPayload = {
    name: form.name.trim(),
    email: form.email.trim(),
    currency: form.currency.trim(),
    address1: form.address1.trim(),
    city: form.city.trim(),
    country: form.country.trim(),
    country_code: form.country_code.trim(),
    subdomain: form.subdomain.trim(),
    opportunity_id: form.opportunity_id.trim(),
    sender_name: form.sender_name.trim(),
    support_email: form.support_email.trim(),
    from_email: form.from_email.trim(),
  };

  const address2 = form.address2.trim();
  if (address2) {
    payload.address2 = address2;
  }

  const region = form.region.trim();
  if (region) {
    payload.region = region;
  }

  const postalCode = form.postal_code.trim();
  if (postalCode) {
    payload.postal_code = postalCode;
  }

  const phone = form.phone.trim();
  if (phone) {
    payload.phone = phone;
  }

  return payload;
}

export function createLoopAnywhereShop (payload: CreateLoopAnywhereShopPayload) {
  return apiClient.post<CreateLoopAnywhereShopResponse>('/setup/commercedata/hydrate', payload);
}

export function activateLoopAnywhereShop (shopId: number | string) {
  return apiClient.post<ActivateLoopAnywhereShopResponse>(`/setup/commercedata/${shopId}/finalize`);
}
